import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingIntakes, markIntakeProcessed, createClient_, updateClient, getProfiles, createTask, ensureEntityFolder } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { parseLocalDate } from '../lib/dateUtils';
import { supabase } from '../lib/supabase';
import { listPhysicians, listFacilities, addCareTeamMember, facilityAddress, fmtPhone } from '../lib/directory';
import PhysicianPicker from '../components/directory/PhysicianPicker';
import PhysicianForm from '../components/directory/PhysicianForm';
import { RefCode, Modal } from '../components/directory/ui';
import { petSummary } from '../lib/pets';
import { WEBSITE_HOST } from '../lib/org';

export default function IntakeSubmissions() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [submitError, setSubmitError] = useState({});

  useEffect(() => {
    getPendingIntakes().then(({ data }) => { setSubmissions(data || []); setLoading(false); });
  }, []);

  // Physician resolution: a parent either picked a doctor from the public
  // list (physician_id set) or typed one (queued for review). The
  // coordinator links it here; the queue item closes itself by trigger.
  const [physicians, setPhysicians] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [resolving, setResolving] = useState(null);   // submission
  const [creating, setCreating] = useState(null);     // { sub, query }
  useEffect(() => {
    listPhysicians().then(({ data }) => setPhysicians((data || []).filter(p => p.status === 'active')));
    listFacilities().then(({ data }) => setFacilities(data || []));
  }, []);
  const physicianFor = (sub) => physicians.find(p => p.id === sub.physician_id) || null;
  const linkPhysician = async (sub, physician) => {
    const { error } = await supabase.from('intake_submissions').update({ physician_id: physician.id }).eq('id', sub.id);
    if (error) { setSubmitError(prev => ({ ...prev, [sub.id]: error.message })); return; }
    setSubmissions(prev => prev.map(x => x.id === sub.id ? { ...x, physician_id: physician.id } : x));
    setResolving(null);
  };

  const handleApprove = async (sub) => {
    setProcessing(sub.id);
    const linkedDoc = physicianFor(sub);
    const { data: client, error } = await createClient_({
      first_name: sub.first_name,
      last_name: sub.last_name,
      date_of_birth: sub.date_of_birth,
      gender: sub.gender,
      address: sub.address,
      city: sub.city,
      county: sub.county,
      state: sub.state,
      zip: sub.zip,
      medicaid_id: sub.medicaid_id,
      private_insurance: sub.private_insurance,
      guardian_first_name: sub.guardian_first_name,
      guardian_last_name: sub.guardian_last_name,
      guardian_relationship: sub.guardian_relationship,
      guardian_phone: sub.guardian_phone,
      guardian_email: sub.guardian_email,
      guardian_address: sub.guardian_address,
      emergency_first_name: sub.emergency_first_name,
      emergency_last_name: sub.emergency_last_name,
      emergency_relationship: sub.emergency_relationship,
      emergency_phone: sub.emergency_phone,
      emergency_email: sub.emergency_email,
      emergency_address: sub.emergency_address,
      diagnosis_summary: sub.diagnosis_summary,
      // linked physician → the directory's details; otherwise what the parent typed
      ...(linkedDoc ? {
        doctor_name: `${linkedDoc.first_name} ${linkedDoc.last_name}`.trim() + (linkedDoc.credentials ? `, ${linkedDoc.credentials}` : ''),
        doctor_npi: linkedDoc.npi || null, doctor_practice: linkedDoc.facility_name || null,
        doctor_address: facilityAddress(linkedDoc) || null,
        doctor_phone: linkedDoc.phone ? fmtPhone(linkedDoc.phone) : null, doctor_fax: linkedDoc.fax ? fmtPhone(linkedDoc.fax) : null,
      } : {
        doctor_name: sub.doctor_name,
        doctor_practice: sub.doctor_practice,
        doctor_address: sub.doctor_address,
        doctor_phone: sub.doctor_phone,
      }),
      referral_source: sub.referral_source,
      consent_texts_emails: sub.consent_texts_emails,
      esignature: sub.esignature,
      service_type: sub.service_type || sub.client_type,
      service_lines: sub.service_lines,
      insurance_name: sub.insurance_name,
      household_pets: sub.household_pets,
      household_pets_other: sub.household_pets_other,
      household_pets_notes: sub.household_pets_notes,
      current_status: 'Intake Form Submitted',
      intake_complete: true,
      created_by: profile.id,
      responsible_person_name: profile.full_name,
      responsible_person_id: profile.id,
    });

    if (error) { setSubmitError(prev => ({ ...prev, [sub.id]: error.message })); setProcessing(null); return; }

    if (sub.physician_id) await addCareTeamMember(client.id, sub.physician_id, 'pcp', profile.full_name);
    await markIntakeProcessed(sub.id, client.client_id);
    ensureEntityFolder('client', client.client_id).then(folderId => folderId && updateClient(client.id, { drive_folder_id: folderId }));

    // Auto-nudge tasks so intake documentation isn't missed (HFRD deadlines).
    const due = (days) => format(new Date(Date.now() + days * 86400000), 'yyyy-MM-dd');
    const clientName = `${client.first_name} ${client.last_name}`;
    const base = { client_id: client.id, client_name: clientName, client_ref: client.client_id, status: 'Open', created_by_id: profile.id, created_by_name: profile.full_name };
    await Promise.all([
      createTask({ ...base, title: `Start & complete signing packet — ${clientName}`, priority: 'Urgent', due_date: due(7),
        description: 'Service Agreement due by 2nd visit or within 7 days; Service Plan within 7 working days of service start (Rule 111-8-65-.09/.11).' }),
      createTask({ ...base, title: `Complete intake / nurse assessment — ${clientName}`, priority: 'Urgent', due_date: due(7),
        description: 'RN completes the intake assessment before services commence (Rule 111-8-65-.10/.11).' }),
    ]).catch((e) => console.warn('Auto-task creation failed:', e));

    setSubmissions(prev => prev.filter(s => s.id !== sub.id));
    setProcessing(null);
    navigate(`/clients/${client.id}`);
  };

  const handleDismiss = async (sub) => {
    await markIntakeProcessed(sub.id, 'DISMISSED');
    setSubmissions(prev => prev.filter(s => s.id !== sub.id));
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1, padding: '12px 16px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 10, fontSize: 13, color: 'var(--accent)' }}>
          New intake submissions from <strong>{WEBSITE_HOST.replace(/^www\./, '')}/intake</strong> appear here. Review each one and click "Create Client" to add them to the CRM, or dismiss if invalid.
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/intake')}>+ New Intake</button>
      </div>

      {loading ? (
        <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
      ) : submissions.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <p style={{ fontWeight: 500, color: 'var(--text)' }}>All caught up!</p>
            <p>No pending intake submissions.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {submissions.map(sub => (
            <div key={sub.id} className="card">
              <div className="card-header">
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{sub.last_name}, {sub.first_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                    Submitted {format(new Date(sub.submitted_at), 'MMM d, yyyy h:mm a')}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  {submitError[sub.id] && (
                    <div style={{ fontSize: 12, color: 'var(--danger)', background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 6, padding: '6px 10px' }}>
                      ⚠ {submitError[sub.id]}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => handleDismiss(sub)}>Dismiss</button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleApprove(sub)}
                      disabled={processing === sub.id}
                    >
                      {processing === sub.id ? <span className="loading-spinner" style={{ borderTopColor: '#fff' }} /> : null}
                      Create Client Record
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 24px' }}>
                {[
                  ['DOB', sub.date_of_birth ? format(parseLocalDate(sub.date_of_birth), 'MMM d, yyyy') : '—'],
                  ['Gender', sub.gender],
                  ['County', sub.county],
                  ['Guardian', sub.guardian_last_name ? `${sub.guardian_last_name}, ${sub.guardian_first_name || ''}`.trim() : (sub.guardian_first_name || '')],
                  ['Phone', sub.guardian_phone],
                  ['Email', sub.guardian_email],
                  ['Medicaid ID', sub.medicaid_id || 'Not provided'],
                  ['Private Insurance', sub.private_insurance ? 'Yes' : 'No'],
                  ['Referral Source', sub.referral_source || '—'],
                  ['Doctor', (() => { const p = physicianFor(sub); return p
                      ? <span><RefCode code={p.ref_code} /> {p.display_name} <span style={{ color: 'var(--success)' }}>✓ linked</span></span>
                      : sub.doctor_name
                        ? <span>{sub.doctor_name}{sub.doctor_practice ? ` · ${sub.doctor_practice}` : ''} <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 6, padding: '1px 8px', fontSize: 11 }} onClick={() => setResolving(sub)}>Match to directory</button></span>
                        : '—'; })()],
                  ['Emergency Contact', sub.emergency_last_name ? `${sub.emergency_last_name}, ${sub.emergency_first_name || ''}`.trim() : (sub.emergency_first_name || '—')],
                  ['Emerg. Phone', sub.emergency_phone || '—'],
                ].map(([label, value]) => (
                  <div key={label} style={{ fontSize: 12 }}>
                    <span style={{ color: 'var(--text3)' }}>{label}: </span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
                {sub.diagnosis_summary && (
                  <div style={{ gridColumn: '1 / -1', fontSize: 12, marginTop: 4 }}>
                    <span style={{ color: 'var(--text3)' }}>Diagnosis summary: </span>
                    <span style={{ color: 'var(--text)' }}>{sub.diagnosis_summary}</span>
                  </div>
                )}
                <div style={{ gridColumn: '1 / -1', fontSize: 12, marginTop: 4 }}>
                  <span style={{ color: 'var(--text3)' }}>Household pets: </span>
                  <span style={{ color: 'var(--text)' }}>{petSummary(sub)}{sub.household_pets_notes ? ` — ${sub.household_pets_notes}` : ''}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {resolving && (
        <Modal onClose={() => setResolving(null)} width={560} title={<>Match <span style={{ fontWeight: 400 }}>“{resolving.doctor_name}”</span> to the directory</>}>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 8 }}>
              The parent typed: <b>{resolving.doctor_name}</b>{resolving.doctor_practice ? ` · ${resolving.doctor_practice}` : ''}{resolving.doctor_phone ? ` · ${resolving.doctor_phone}` : ''}{resolving.doctor_address ? ` · ${resolving.doctor_address}` : ''}
            </div>
            <PhysicianPicker physicians={physicians} value={null} autoFocus onChange={(p) => p && linkPhysician(resolving, p)}
              onAddNew={(q) => { setCreating({ sub: resolving, query: q || resolving.doctor_name }); setResolving(null); }} />
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 10 }}>What the parent typed stays on the submission; the link is added beside it and carries onto the client as their PCP.</div>
          </div>
        </Modal>
      )}
      {creating && (
        <PhysicianForm initialQuery={creating.query} facilities={facilities}
          onSaved={(doc) => { const sub = creating.sub; setCreating(null); setPhysicians(ps => [...ps, { ...doc, display_name: `${doc.last_name}, ${doc.first_name}` }]); linkPhysician(sub, doc); }}
          onClose={() => setCreating(null)} />
      )}
    </div>
  );
}
